import React, {useEffect, useState} from "react";
import styled from "styled-components";
import tw from "twin.macro";
import plusIcon from "../../../images/icon/plus.png";
import {getAuth} from "firebase/auth";
import {collection, doc, getDoc, getDocs, getFirestore, query, where} from "firebase/firestore"; // Updated import to follow naming conventions
import { useTranslation } from "react-i18next";

const CardContainer = styled.div`
    ${tw`w-full max-w-2xl mx-auto my-8`}
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border: 4px solid #a0c1aa;
    min-height: 300px;
    border-radius: 20px;

    @media (max-width: 768px) {
        margin-left: 8px;
        width: calc(100% - 20px); /* Adding side margins on mobile */
    }
`;

const CardHeaderContainer = styled.div`
    ${tw`flex`}

    @media (max-width: 768px) {
    ${tw`flex-col`}
}
`;

const CardHeaderLink = styled.a`
    ${tw`text-lg font-bold relative flex-shrink-0 h-auto py-8 no-underline text-secondary-600`}
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    text-align: center;
    padding-right: 20px;
    border-left: 4px solid #a0c1aa;
    padding-left: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    height: auto;
    min-height: 56.1%;

    @media (max-width: 768px) {
        writing-mode: horizontal-tb;
        transform: rotate(0);
        padding: 10px;
        border-left: none;
        border-bottom: 3px solid #a0c1aa;
    }
`;

const CardContentContainer = styled.div`
    ${tw`flex flex-col p-8 w-full`}
    width: 70%;

    @media (max-width: 768px) {
        ${tw`w-full`}
        padding: 4px; /* Уменьшение padding для мобильных устройств */
    }
`;

const Icon = styled.img`
    width: 18px;
    height: 18px;
    margin-right: 15px; // Add some space between the icon and text
`;

const CardContent = styled.div`
    ${tw`text-secondary-600  font-bold  mb-6 text-xl `}
`;

const CardStats = styled.div`
    ${tw`flex flex-col text-secondary-600 text-base`}
`;

const Stat = styled.div`
    ${tw`mb-2 flex justify-between`}
`;

const StatLabel = styled.div`
    ${tw`flex items-center text-base xl:text-lg text-secondary-600`}
`;

const StatValue = styled.div`
    ${tw`ml-2 text-right mr-6 text-base xl:text-lg text-secondary-600`}
`;

const CardActions = styled.div`
    ${tw`mt-auto flex justify-between items-center`}
    gap: 16px;

    @media (max-width: 768px) {
        ${tw`flex-wrap w-full justify-between`}
        gap: 12px;
    }
`;

const Button = styled.button`
    ${tw`flex items-center justify-center py-4 text-white rounded-lg font-bold transition duration-300 ease-in-out hover:bg-purple-600 focus:outline-none`}
    svg {
        ${tw`mr-2`}
    }
    border: none;
    background-color:  #0abd19;
    width: 48%; /* Ensure buttons take 48% width to fit in one line */
    min-width: 200px;

    @media (max-width: 768px) {
        ${tw`w-full mb-4`}
    }
`;

const PlusIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 2C12.5523 2 13 2.44772 13 3V11H21C21.5523 11 22 11.4477 22 12C22 12.5523 21.5523 13 21 13H13V21C13 21.5523 12.5523 22 12 22C11.4477 22 11 21.5523 11 21V13H3C2.44772 13 2 12.5523 2 12C2 11.4477 2.44772 11 3 11H11V3C11 2.44772 11.4477 2 12 2Z"
            fill="currentColor"
        />
    </svg>
);

const Navlink = styled.a`
    text-decoration: none;
    ${tw`flex items-center justify-center text-white font-bold w-full`}
`;

const WarehouseServiceCard = () => {
    const { t } = useTranslation();
    const [processedCount, setProcessedCount] = useState(0);
    const [inTransitCount, setInTransitCount] = useState(0);
    const [sentCount, setSentCount] = useState(0);
    const [arrivedCount, setArrivedCount] = useState(0);
    const [transferredCount, setTransferredCount] = useState(0);
    const [deliveredCount, setDeliveredCount] = useState(0);
    const fetchParcelCounts = async () => {
        const auth = getAuth();
        const user = auth.currentUser;
        const db = getFirestore();

        if (user) {
            try {
                const userDocRef = doc(db, 'users', user.uid);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    // Use userId from userData, or fallback to user.uid if not set
                    const userId = userData.userId || user.uid;

                    if (userId) {
                        const processedQuery = query(collection(db, 'applications'), where("userId", "==", userId), where("status", "==", "Создана"));

                        const processedSnapshot = await getDocs(processedQuery);

                        setProcessedCount(processedSnapshot.docs.length);
                    } else {
                        console.warn('userId is undefined, cannot fetch warehouse services counts');
                    }
                } else {
                    console.warn('User document does not exist in Firestore');
                }
            } catch (error) {
                console.error('Error fetching warehouse services counts:', error);
            }
        }
    };
    useEffect(() => {
        fetchParcelCounts();
    }, []);


    return (

            <CardContainer>
                <CardHeaderContainer>
                    <CardHeaderLink href="/WarehouseServices">
                        {t("warehouses.title")}
                    </CardHeaderLink>
                    <CardContentContainer>
                        <CardContent>
                            {t("warehouses.description")}
                        </CardContent>

                        <CardStats>
                            <Stat>
                                <StatLabel><div>{t("warehouses.accepted")}</div></StatLabel>
                                <StatValue>{processedCount} {t("commons.items")}</StatValue>
                            </Stat>
                            <Stat>
                                <StatLabel><div>{t("warehouses.processing")}</div></StatLabel>
                                <StatValue>0 {t("commons.items")}</StatValue>
                            </Stat>
                            <Stat>
                                <StatLabel><div>{t("warehouses.completed")}</div></StatLabel>
                                <StatValue>0 {t("commons.items")}</StatValue>
                            </Stat>
                            <Stat>
                                <StatLabel><div>{t("warehouses.rejected")}</div></StatLabel>
                                <StatValue>0 {t("commons.items")}</StatValue>
                            </Stat>
                        </CardStats>

                        <CardActions>
                            <Navlink href="/ServisApplication">
                                <Button>
                                    <Icon src={plusIcon} alt="Plus Icon" />
                                    {t("warehouses.addRequest")}
                                </Button>
                            </Navlink>
                            <Navlink href="/WarehouseServices">
                                <Button>{t("warehouses.viewServices")}</Button>
                            </Navlink>
                        </CardActions>
                    </CardContentContainer>
                </CardHeaderContainer>
            </CardContainer>
    );
};

export default WarehouseServiceCard;
